<template>
  <div id='app-'>
    <seo vif="frontMatterHasValues()"
         :title="frontmatter.title || ''"
         :description="frontmatter.description || ''"
         :image="`https://kobina.me${frontmatter.cover}`"
         :url="`https://kobina.me/blogs/${slug}`"
         :type="'website'"
         :publishedTime="frontmatter.date || ''"
         :author="frontmatter.author || 'Kobina Koomson'"
         :twitterCard="'summary_large_image'"
         :twitterHandle="'@kobina_koomson'"
         :siteName="'Kobina Koomson'"
    />
    <nav-bar v-slot:nav-footer>
      <div class="post-title d-flex align-items-center z-index-1 text-white">
        <div class=" pb-3 pb-lg-4 pt-2 text-center w-100">
          <h3 class="text-center mb-0">{{ frontmatter.title }}</h3>
        </div>
      </div>
    </nav-bar>
    <b-container class="mb-8">
      <b-row class=" main-content" :style="{ marginTop: `${navHeight}px` }">
        <b-col class="position-relative">
          <b-row class="mb-4">
            <b-col>
              <img height="400" :src="frontmatter.cover" :alt="formatter.coverAlt"
                   class="img-fluid cover-image rounded-18 shadow-lg" />
            </b-col>
          </b-row>

          <b-row class="mt-6" md="">
            <b-col lg="9" class="mb-5 mt-lg-0 post-body-col">
              <div class="blog-post-content" v-html="html" />
            </b-col>
            <b-col lg="3">
              <b-card class="d-flex bg-dark flex-column gap-4 post-meta sticky-top">
                <b-row>
                  <b-col class="col-12 col-md-3 col-lg-12">
                    <div class="post-author">
                      <h6 class=" font-weight-600">{{ frontmatter.author }}</h6>
                    </div>
                    <div class="post-date mt-2 mb-3 text-muted">
                      {{ formatDate(frontmatter.date) }}
                    </div>
                  </b-col>
                  <b-col class="post-tags">
                    <p class="leading-none d-in"><strong>Tags</strong></p>
                    <span v-for="(tag, index) in frontmatter.tags" :key="index" class="tag mr-1 my-1 d-inline-block">{{
                        tag
                      }}</span>
                  </b-col>
                </b-row>
                <b-row class="mt-4 pt-3 justify-content-start justify-content-lg-center align-items-center">
                  <b-col class="col-auto col-lg-5 col-md-6 d-flex align-items-center pl-0">
                    <div class="clap-container">
                      <button @click="clap" :class="{ 'clap-active': animating }"
                              class="clap-button text-primary position-relative">
                        👏
                        <span class="clap-count position-absolute">{{ claps }}</span>
                      </button>
                      <transition name="float">
                        <span v-if="showBurst" class="clap-burst">+1</span>
                      </transition>

                    </div>
                  </b-col>
                  <div
                    class="col-auto col-lg-auto col-md-6 d-flex align-items-center justify-content-center h-100 border-left pr-0">
                    <b-button variant="primary" class="p-0 px-2" @click="openModal">
                      Share
                      <Icon name="share" :size="16" class="ml-1" />
                    </b-button>
                    <b-modal
                      id="share-modal"
                      ref="shareModal"
                      size="md"
                      centered
                      header-class="share-modal-header border-bottom-0"
                      body-class="pb-5 pt-3"
                      footer-class="border-top-0 justify-content-center"
                      hide-footer
                    >
                      <div style="min-height: 250px;"
                           class="d-flex flex-column justify-content-center align-items-center">
                        <h4 class="mb-5 text-center font-weight-500">Share this post</h4>
                        <share-buttons :title="frontmatter.title" :url="`https://kobina.me/blog/${postSlug}.html`" />
                      </div>
                    </b-modal>
                  </div>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row class="mt-5" v-if="relatedPosts.length > 0">
        <b-col>
          <h4 class="mb-4">Similar</h4>
          <div class="d-flex flex-column gap-3">
            <b-row>
              <b-col md="6" v-for="post in relatedPosts" :key="post.slug" class="mt-3">
                <router-link :to="`/blog/${post.slug}`"
                             class="text-decoration-none w-100 h-100 mt-3">

                  <b-card no-body class="overflow-hidden similar-post-card">
                    <b-row no-gutters>
                      <b-col class="p-3 col-auto">
                        <img :src="post.cover" height="100" width="100" style="object-fit: cover" alt="post.coverAlt"
                             class="rounded-8" />
                      </b-col>
                      <b-col>
                        <b-card-body>
                          <h5>{{ post.title }}</h5>
                          <p class="text-muted">{{ formatDate(post.date) }}</p>
                        </b-card-body>
                      </b-col>
                    </b-row>
                  </b-card>
                </router-link>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-container>
    <mini-footer />

  </div>
</template>
<script>
import matter from "gray-matter";
import MarkdownIt from "markdown-it";
import hljs from "highlight.js";
import "highlight.js/styles/devibeans.css";
import NavBar from "@/layout-components/nav.vue";
import { formatter } from "@vue/cli-service/lib/util/resolveLoaderError";
import Seo from "@/components/Seo.vue";
import dayjs from "dayjs";
import { getTwoRandomPosts } from "@/helpers/utils";
import { getPostClapsInRealTime, updatePostClaps } from "@/helpers/firebase";
import Icon from "@/components/Icon.vue";
import ShareButtons from "@/views/ShareButtons.vue";
// TODO: add two other blg posts suggestions at the bottom of the post
//  add a "Back to Blogs" button
//  add a "Share" buttons
// set the title of the page to the post title
// set the meta description to the post summary
// set the og:image to the post image if available
//  add a "Read more" button at the end of the post
//  add a "Comments" section at the end of the post

function isEmptyString (str) {
  return (!str || str.length === 0 || !str.trim() || str === "" || /^\s*$/.test(str));
}

// breathing room between the bottom of the fixed nav and the top of the cover
const NAV_COVER_GAP = 16;

export default {
  name: "BlogPost",
  components: { ShareButtons, Icon, NavBar, Seo },
  props: [
    "post",
    "content",
    "slug"
  ],
  data () {
    return {
      frontmatter: {},
      html: "",
      relatedPosts: [],
      navHeight: 0,
      claps: 0,
      animating: false,
      showBurst: false
    };
  },
  computed: {
    postSlug () {
      return this.slug.split(".")[0] || "unknown-post";
    }
  },
  methods: {
    formatDate (dateString) {
      return dayjs(dateString).format("D MMM, YYYY");
    },
    formatter,
    async loadMarkdown () {
      try {
        // Dynamically import the Markdown file based on the slug
        const rawMarkdown = await import(`@/blogs/${this.slug}`);

        // Parse the Markdown content and extract front matter
        const { data, content } = matter(rawMarkdown.default || rawMarkdown);

        // Initialize Markdown-it with syntax highlighting
        const md = new MarkdownIt({
          html: true,
          linkify: true,
          typographer: true,
          breaks: true,
          highlight: function (str, lang) {
            if (lang && hljs.getLanguage(lang)) {
              try {
                return `<pre class="hljs"><code>${hljs.highlight(str, { language: lang }).value}</code></pre>`;
              } catch (e) {
                console.error(`Error highlighting code: ${e}`);
              }
            }
            return `<pre class="hljs"><code>${md.utils.escapeHtml(str)}</code></pre>`;
          }
        });

        // Inside your loadMarkdown method, after initializing md:
        md.core.ruler.after("block", "custom_blockquote", function (state) {
          state.tokens.forEach((token, idx) => {
            if (token.type === "blockquote_open") {
              const nextToken = state.tokens[idx + 2];
              console.log({ nextToken });
              if (nextToken && nextToken.type === "inline") {
                const content = nextToken.content.trim();

                if (!isEmptyString(content)) {
                  const lowerCaseContent = content.toLowerCase();

                  if (lowerCaseContent.startsWith("info:")) {
                    token.attrSet("class", "blockquote-info");
                    nextToken.content = content.replace(/^info:\s*/i, "");
                  } else if (lowerCaseContent.startsWith("warning:")) {
                    token.attrSet("class", "blockquote-warning");
                    nextToken.content = content.replace(/^warning:\s*/i, "");
                  } else if (lowerCaseContent.startsWith("error:")) {
                    token.attrSet("class", "blockquote-error");
                    nextToken.content = content.replace(/^error:\s*/i, "");
                  } else if (lowerCaseContent.startsWith("success:")) {
                    token.attrSet("class", "blockquote-success");
                    nextToken.content = content.replace(/^success:\s*/i, "");
                  } else if (lowerCaseContent.startsWith("hint:")) {
                    token.attrSet("class", "blockquote-purple");
                    nextToken.content = content.replace(/^hint:\s*/i, "");
                  }
                }
              }
            }
          });
        });

        // Custom renderer for headings to add IDs
        const defaultHeadingRender = md.renderer.rules.heading_open || function (tokens, idx, options, env, self) {
          return self.renderToken(tokens, idx, options);
        };

        md.renderer.rules.heading_open = function (tokens, idx, options, env, self) {
          const token = tokens[idx];
          const title = tokens[idx + 1].content;

          // Generate ID from heading text (convert to lowercase, replace spaces/special chars with hyphens)
          const id = title
            .toLowerCase()
            .replace(/[^\w\s-]/g, "") // Remove special characters except hyphens
            .replace(/\s+/g, "-") // Replace spaces with hyphens
            .replace(/-+/g, "-") // Replace multiple hyphens with a single hyphen
            .trim();

          // Add id attribute to the heading
          token.attrSet("id", id);

          return defaultHeadingRender(tokens, idx, options, env, self);
        };

        // Render Markdown to HTML
        this.html = md.render(content);
        this.frontmatter = data;

        // console.log("getTwoRandomPosts()");
        // console.log(getTwoRandomPosts(data.slug));

        // let create a computed property for related posts
        // this will filter the posts based on the presence of one or more shared tags
        /*  this.relatedPosts = posts.flat().filter(post => {
          return post.tags.some(tag => post.slug + ".md" !== this.slug && this.frontmatter.tags.includes(tag));
        }).slice(0, 1); // Limit to 3 related posts  */

        // const relatedPosts = getRelatedPostsBySlug({ ...data })

        // this.relatedPosts = relatedPosts;

        if ([].length < 1) {
          this.relatedPosts = getTwoRandomPosts(data.slug);
        }
      } catch (err) {
        console.error(`Error loading markdown file: ${this.slug}`, err);
      }
    },
    // The nav is fixed and carries the post title, so its height is whatever
    // the title happens to wrap to. Measuring it once is not enough: the title
    // arrives with the markdown (after mount), re-wraps at every breakpoint,
    // and shifts again when the webfont swaps in. Everything below keeps this
    // in sync so the cover always clears the nav.
    setNavHeight () {
      const navBar = this.$el && this.$el.querySelector(".navbar");
      if (navBar) {
        this.navHeight = navBar.offsetHeight + NAV_COVER_GAP;
      }
    },
    // named wrapper so the resize Event is never passed on as an argument
    onWindowResize () {
      this.setNavHeight();
    },
    loadClaps () {
      getPostClapsInRealTime(this.postSlug, (count) => {
        // console.log("count", count);
        this.claps = count;
      });
    },
    clap () {
      updatePostClaps(this.postSlug);

      this.animating = true;
      this.showBurst = true;

      setTimeout(() => {
        this.animating = false;
        this.showBurst = false;
      }, 500);
    },
    openModal () {
      this.$refs.shareModal.show();
    },
    frontMatterHasValues () {
      return Object.keys(this.frontmatter).length > 0;
    }
  },
  watch: {
    // the title only exists once the markdown has loaded, and it is what makes
    // the nav tall, so re-measure as soon as it lands
    "frontmatter.title" () {
      this.$nextTick(this.setNavHeight);
    }
  },
  mounted () {
    this.setNavHeight();

    // a ResizeObserver catches every cause of the nav changing height, including
    // the ones no event fires for: the title wrapping to another line, the
    // webfont swapping in, the mobile nav expanding
    const navBar = this.$el.querySelector(".navbar");
    if (navBar && typeof ResizeObserver !== "undefined") {
      this.navObserver = new ResizeObserver(() => this.setNavHeight());
      this.navObserver.observe(navBar);
    }
    window.addEventListener("resize", this.onWindowResize);
  },
  beforeCreate () {
  },
  beforeDestroy () {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.navObserver) this.navObserver.disconnect();
  },
  created () {
    this.loadMarkdown();
    this.loadClaps();
  },
  destroyed () {
  },
  watch: {
    $route () {
      this.loadMarkdown();
      this.loadClaps();
    },
    slug () {
      this.loadMarkdown();
      this.loadClaps();
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.slug = to.params.slug;
    this.loadMarkdown();
    this.loadClaps();
    next();
  }
};
</script>
<style lang="scss" scoped>
@import "../assets/styles/scss/includes/mixins";

$title-margin-top: 76px;
$title-height: 100px;
.sticky-top {
  @include _desktop {
    top: $title-margin-top !important;
  }
}

.post-title {
  h3 {
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 0;

    @include _tablet {
      font-size: 1.5rem;
    }

    @include _desktop {
      font-size: 1.75rem;
    }
  }
}

.main-content {
  margin-top: $title-margin-top + $title-height;
}

.post-meta {
  top: $title-margin-top + $title-height !important;
}

.cover-image {
  height: 200px;
  width: 100%;
  object-fit: cover;
  object-position: top center;

  @include _tablet {
    height: 400px;
  }

  @include _mobile {
    height: 500px;
  }
}

.similar-post-card {
  height: 100%;

  img {
    height: 100%;
    min-height: 100px;
    width: 100px;
    object-fit: cover;
    object-position: top center;
  }
}

</style>
<style lang="scss">
:root {
  --blockquote-info-bg: rgba(24, 144, 255, 0.12);
  --blockquote-info-border: #1890ff;
  --blockquote-info-color: #91d5ff;

  --blockquote-warning-bg: rgba(250, 173, 20, 0.12);
  --blockquote-warning-border: #faad14;
  --blockquote-warning-color: #ffe58f;

  --blockquote-error-bg: rgba(255, 77, 79, 0.12);
  --blockquote-error-border: #ff4d4f;
  --blockquote-error-color: #ff7875;

  --blockquote-success-bg: rgba(10, 163, 10, 0.12);
  --blockquote-success-border: #00a30a;
  --blockquote-success-color: #a1f8a1;

  --blockquote-purple-bg: rgba(128, 0, 128, 0.12);
  --blockquote-purple-border: #800080;
  --blockquote-purple-color: #e0e0ff;

  --inline-code-color: #ffc200;
}

// the pastel callout/code colours above are tuned for dark backgrounds;
// the light scheme needs dark ink of the same hues to stay readable
:root[data-theme="light"] {
  --blockquote-info-color: #0b5cad;
  --blockquote-warning-color: #7a4d00;
  --blockquote-error-color: #a8121a;
  --blockquote-success-color: #0c7a14;
  --blockquote-purple-color: #6a1b9a;

  --inline-code-color: #8a5200;
}

// a flex item defaults to min-width:auto, so an unbreakable child (a long
// snake_case identifier, a wide code block, a table) stretches the column past
// the viewport and the whole page picks up a horizontal scrollbar. Letting the
// column shrink below its content is what keeps the overflow local to the
// element that actually overflows.
.post-body-col {
  min-width: 0;
}

.blog-post-content {
  // prose wraps mid-token rather than widening the column. `anywhere` (not
  // `break-word`) is deliberate: only `anywhere` also shrinks the min-content
  // width, which is the part that stops the stretch.
  overflow-wrap: anywhere;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    //font-weight: 700;
    line-height: 1.2;
    scroll-margin-top: 100px;
  }

  pre {
    padding: 1rem;
    border-radius: 12px;
    overflow-x: auto;
    background: #333;
    margin-top: 1rem;
    max-width: 100%;

    // code blocks scroll instead of wrapping: breaking a long line would
    // change what the code means, so the block keeps its own scrollbar
    code {
      border-radius: 12px;
      color: #fff;
      font-family: 'ubuntu mono', 'Courier New', Courier, monospace;
      overflow-wrap: normal;
      word-break: normal;
      white-space: pre;
    }
  }

  code {
    color: var(--inline-code-color, #ffc200);
    font-size: 1.3rem;
    // inline code wraps with the sentence it sits in
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  // wide tables scroll inside their own box rather than widening the article
  table {
    display: block;
    width: fit-content;
    max-width: 100%;
    overflow-x: auto;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  blockquote {
    padding: 1rem 1rem;
    background: var(--card-bg);

    margin-top: 1rem;
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    border-radius: 12px;

    p {
      margin-bottom: 0;
      margin-top: 0;
      margin-block-start: 0;
      margin-block-end: 0;
    }
  }

  .blockquote-info, .blockquote-warning, .blockquote-error, .blockquote-success, .blockquote-purple {
    padding: 1rem 1rem;
    border: 1px solid transparent;
    border-left-width: 5px;
  }

  .blockquote-info {
    background: var(--blockquote-info-bg);
    border-color: var(--blockquote-info-border);

    color: var(--blockquote-info-color);
  }

  .blockquote-warning {
    background: var(--blockquote-warning-bg);
    border-color: var(--blockquote-warning-border);
    color: var(--blockquote-warning-color);
  }

  .blockquote-error {
    background: var(--blockquote-error-bg);
    border-color: var(--blockquote-error-border);
    color: var(--blockquote-error-color);
  }

  .blockquote-success {
    background: var(--blockquote-success-bg);
    border-color: var(--blockquote-success-border);
    color: var(--blockquote-success-color);
  }

  .blockquote-purple {
    background: var(--blockquote-purple-bg);
    border-color: var(--blockquote-purple-border);
    color: var(--blockquote-purple-color);
  }

  .hljs-meta .hljs-string, .hljs-string, .hljs-subst, .hljs-symbol, .hljs-bullet, .hljs-addition {
    color: #6aff00;
  }

  ul {
    li {
      margin-left: 16px;
      padding-left: 5px;
      list-style: disc;
      line-height: 1.6;
      margin-top: .3rem;
    }
  }

  ol {
    li {
      margin-left: 16px;
      padding-left: 5px;
      list-style: decimal;
      line-height: 1.6;
      margin-top: .3rem;
    }
  }

  p {
    margin-bottom: 1rem;
    margin-top: 1rem;
    margin-block-start: 1rem;
    margin-block-end: 1rem;
    line-height: 1.6;
    font-size: 1.205rem;
  }

  // let's style tables; theads should be bold and the rest should be normal
  // rows should be underlined (border bottom), whereas the first row should have a thicker underline
  // cells should be left-aligned
  table {
    width: 100%;
    border-collapse: collapse;

    th, td {
      border-collapse: collapse;
    }

    th {
      padding: 20px 10px;
    }

    td {
      padding: 10px 10px;
    }

    thead {
      th {

        border-bottom: 3px solid var(--outline);
      }
    }

    tr {
      td {
        text-align: left;
        border-bottom: 1px solid var(--outline);
      }
    }
  }
}

.modal-header {
  .close {
    font-size: 3rem;
    font-weight: 300;
    margin-right: 0;
  }
}
</style>
<style scoped lang="scss">
.clap-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.clap-button {
  font-size: 1.8rem;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  transition: transform 0.2s ease;

  width: 60px;
  height: 60px;
  border-radius: 50%;
  //border: 2px solid #ff0003;
}

.clap-button.clap-active {
  transform: scale(1.4);
}

.clap-count {
  display: block;
  min-width: 30px;
  height: 30px;
  background: #ff0003;
  // the badge keeps its red in both themes, so the digits stay white rather
  // than inheriting the theme's text colour and going black in light mode
  color: #fff;
  padding: 4px;
  font-size: 1.1rem;
  font-weight: 600;
  -webkit-border-radius: 50%;
  -moz-border-radius: 50%;
  border-radius: 50%;
  top: -15px;
  right: -15px;
}

.clap-burst {
  position: absolute;
  left: 10px;
  top: -20px;
  font-size: 1rem;
  color: #28a745;
  animation: float-up 0.5s ease-out;
  pointer-events: none;
}

@keyframes float-up {
  0% {
    transform: translateY(0);
    opacity: 1;
  }
  100% {
    transform: translateY(-20px);
    opacity: 0;
  }
}

.float-enter-active,
.float-leave-active {
  transition: opacity 0.5s;
}

.float-enter,
.float-leave-to {
  opacity: 0;
}

//.blog-post-content {
// default style
//blockquote {
//  padding: .5rem 1rem;
//  background: var(--card-bg);
//  font-style: italic;
//  margin-top: 1rem;
//  margin-bottom: 1rem;
//  border-radius: 5px;
//}

//}
</style>
